import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  useColorMode,
  Container,
  Alert,
  AlertIcon,
  AlertDescription,
  Flex,
  Image,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../store/authStore";
import { API_ENDPOINTS } from "../constants";
import { useLocation, useNavigate } from "react-router-dom";

const Login: React.FC<{ redirectTo?: string }> = ({ redirectTo = "/" }) => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const routeState = (location.state as { redirectTo?: string } | null) ?? null;
  const effectiveRedirectTo = routeState?.redirectTo ?? redirectTo;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getErrorMessage = (code?: string, fallback?: string) => {
    const tInvalid = t("login.invalidCredentials");
    const invalidMsg =
      tInvalid && tInvalid !== "login.invalidCredentials"
        ? tInvalid
        : "Invalid username or password";

    const tGeneric = t("login.error");
    const genericMsg =
      tGeneric && tGeneric !== "login.error" ? tGeneric : "Login failed";

    if (code === "AUTHENTICATION_ERROR") {
      return invalidMsg;
    }
    if (code === "VALIDATION_ERROR") {
      return genericMsg;
    }
    return fallback || genericMsg;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch(API_ENDPOINTS.USER_LOGIN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const err = new Error(data.message || "Login failed");
        (err as any).error = data.error;
        throw err;
      }

      if (data.success && data.data) {
        // Store authentication data
        login(
          data.data.username,
          data.data.userId,
          data.data.accessToken,
          data.data.expiresAt,
          data.data.type || null,
          data.data.firstName || null,
          data.data.lastName || null,
        );

        // Redirect based on role and target
        const canCreate =
          data.data.type === "contributor" || data.data.type === "admin";
        if (effectiveRedirectTo === "/create" && !canCreate) {
          navigate("/");
        } else {
          navigate(effectiveRedirectTo);
        }
      } else {
        const err = new Error(data.message || "Login failed");
        (err as any).error = data.error;
        throw err;
      }
    } catch (err) {
      const errorCode =
        err && typeof err === "object" ? (err as any).error : undefined;
      console.log(errorCode);
      const fallbackMessage =
        err instanceof Error ? err.message : t("login.error") || "Login failed";
      setError(getErrorMessage(errorCode, fallbackMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container id="login-container" maxW="container.md" py={6} bg="transparent">
      <Flex
        direction={{ base: "column", md: "row" }}
        align="center"
        justify="center"
        gap={{ base: 4, md: 8 }}
        w="100%"
      >
        <Box
          id="login-illustration"
          flex={{ base: "0 0 auto", md: "1 1 45%" }}
          maxW={{ base: "214px", md: "245px" }}
          mx={{ base: "auto", md: 0 }}
        >
          <Image
            src="/undraw_bibliophile_sbt0.svg"
            alt=""
            role="presentation"
            w="100%"
            h="auto"
            opacity={colorMode === "dark" ? 0.9 : 1}
          />
        </Box>
        <Box
          id="login-card"
          flex={{ base: "0 0 auto", md: "1 1 55%" }}
          bg="transparent"
          p={{ base: 4, md: 5 }}
          borderRadius="md"
          boxShadow="md"
          maxW="md"
          w="100%"
        >
          <VStack id="login-content" spacing={5} align="stretch">
            <Heading id="login-title" size="lg" textAlign="center">
              {t("login.title") || "Login"}
            </Heading>

            {error && (
              <Alert id="login-error-alert" status="error" borderRadius="md">
                <AlertIcon id="login-error-icon" />
                <AlertDescription id="login-error-message">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form id="login-form" onSubmit={handleSubmit}>
              <VStack id="login-form-fields" spacing={4} align="stretch">
                <FormControl id="login-username-control" isRequired>
                  <FormLabel htmlFor="login-username-input">
                    {t("login.username") || "Username"}
                  </FormLabel>
                  <Input
                    id="login-username-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={
                      t("login.usernamePlaceholder") || "Enter your username"
                    }
                    bg={colorMode === "light" ? "white" : "gray.600"}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                    }}
                  />
                </FormControl>

                <FormControl id="login-password-control" isRequired>
                  <FormLabel htmlFor="login-password-input">
                    {t("login.password") || "Password"}
                  </FormLabel>
                  <Input
                    id="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={
                      t("login.passwordPlaceholder") || "Enter your password"
                    }
                    bg={colorMode === "light" ? "white" : "gray.600"}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                    }}
                  />
                </FormControl>

                <Button
                  id="login-submit-button"
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  width="100%"
                  isLoading={isSubmitting}
                  loadingText={t("login.submitting") || "Logging in..."}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "md",
                  }}
                  transition="all 0.2s"
                >
                  {t("login.submit") || "Login"}
                </Button>
              </VStack>
            </form>
          </VStack>
        </Box>
      </Flex>
    </Container>
  );
};

export default Login;
